import { Box, Fab, Tooltip } from '@mui/material';
import { RegridResponse } from '../../models/regrid.model.tsx';

import {
  Viewer,
  CameraLookAt,
  Cesium3DTileset,
  CesiumComponentRef,
  Entity,
  ModelGraphics,
} from 'resium';
import {
  Cartesian3,
  Cesium3DTileset as TileSet,
  Viewer as CesiumViewer,
  Ion,
  HeightReference,
  CesiumTerrainProvider,
  ClippingPolygon,
  ClippingPolygonCollection,
  Transforms,
  HeadingPitchRoll,
  Color,
} from 'cesium';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useRef,
  useState,
  useEffect,
} from 'react';
import { useGet3dHomeModels } from '../../api/homes.ts';
import UpdateIcon from '@mui/icons-material/Update';
import RestoreIcon from '@mui/icons-material/Restore';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import RemoveIcon from '@mui/icons-material/Remove';
import SettingsIcon from '@mui/icons-material/Settings';
import './3d-home-viewer.scss';
import { HomePlacement } from './types/position.ts';
import { expandPolygonCoordinates } from './utils/coords-math.ts';

interface CesiumHomeViewerProps {
  selectedHomeModelSku: string;
  regridData: RegridResponse;
  homePlacement: HomePlacement;
  setHomePlacement: Dispatch<SetStateAction<HomePlacement>>;
  selectedOverlay?: string;
}

// Needed for now to access 3d tiles on localhost. This will likley be removed once we have our google tiles keys are configured.
Ion.defaultAccessToken = import.meta.env.VITE_CESIUM_API_KEY as string;
const tilesUrl = `https://tile.googleapis.com/v1/3dtiles/root.json?key=${import.meta.env.VITE_GOOGLE_TILES_API_KEY}`;

export const ThreeDimensionHomeViewer = (props: CesiumHomeViewerProps) => {
  const { regridData, homePlacement, setHomePlacement, selectedHomeModelSku } =
    props;
  const { geometry } = regridData.parcelData;
  const { data: homeModel } = useGet3dHomeModels(selectedHomeModelSku);

  const offset = new Cartesian3(0, 0, 150);
  const coordinates = geometry.coordinates[0];
  const ref = useRef<CesiumComponentRef<CesiumViewer> | null>(null);
  const tilesetRef = useRef<TileSet | null>(null);

  // Cleanup function
  useEffect(() => {
    return () => {
      setTimeout(() => {
        try {
          if (
            ref.current?.cesiumElement &&
            !ref.current.cesiumElement.isDestroyed
          ) {
            const viewer = ref.current.cesiumElement;
            viewer.clock.shouldAnimate = false;
            viewer.entities.removeAll();
            viewer.dataSources.removeAll();
            viewer.scene.primitives.removeAll();
            viewer.destroy();
          }

          if (tilesetRef.current && !tilesetRef.current.isDestroyed) {
            tilesetRef.current.destroy();
            tilesetRef.current = null;
          }
        } catch (error) {
          console.warn('Error during cleanup:', error);
        }
      }, 500);
    };
  }, []);

  const handleReady = (tileset: TileSet) => {
    try {
      tilesetRef.current = tileset;
      clearSelectedProperty(tileset);
      setCameraControls();
    } catch (error) {
      console.warn('Error in handleReady:', error);
    }
  };

  const clearSelectedProperty = (tileset: TileSet) => {
    if (!coordinates || coordinates.length < 3) {
      console.error('Not enough coordinates to form a polygon');
      return;
    }

    // Ensure the polygon is closed by adding the first point at the end if needed
    const closedCoordinates = [...coordinates];
    if (
      closedCoordinates.length > 0 &&
      (closedCoordinates[0][0] !==
        closedCoordinates[closedCoordinates.length - 1][0] ||
        closedCoordinates[0][1] !==
          closedCoordinates[closedCoordinates.length - 1][1])
    ) {
      closedCoordinates.push(closedCoordinates[0]);
    }

    const expandedCoordinates = expandPolygonCoordinates(closedCoordinates, 15);

    const cartesianCoordinates = expandedCoordinates
      .map((coord) => {
        if (
          !coord ||
          coord.length !== 2 ||
          isNaN(coord[0]) ||
          isNaN(coord[1])
        ) {
          console.error('Invalid coordinate:', coord);
          return null;
        }
        try {
          return Cartesian3.fromDegrees(coord[0], coord[1]);
        } catch (error) {
          console.error('Error converting coordinate:', coord, error);
          return null;
        }
      })
      .filter(Boolean) as Cartesian3[];

    if (cartesianCoordinates.length < 4) {
      console.error('Not enough valid cartesian coordinates to form a polygon');
      return;
    }

    tileset.clippingPolygons = new ClippingPolygonCollection({
      polygons: [new ClippingPolygon({ positions: cartesianCoordinates })],
    });
  };

  const [showTileset, setShowTileSet] = useState(true);
  const [showSettings, setShowSettings] = useState(true);

  const degreesToRadians = (degrees: number): number => {
    return degrees * (Math.PI / 180);
  };

  const setCameraControls = () => {
    try {
      const viewer = ref.current?.cesiumElement;
      if (!viewer) {
        setTimeout(setCameraControls, 100);
        return;
      }

      const scene = viewer.scene;

      // Camera controls
      scene.screenSpaceCameraController.minimumZoomDistance = 50;
      scene.screenSpaceCameraController.maximumZoomDistance = 250;
      scene.screenSpaceCameraController.maximumTiltAngle = Math.PI / 1.6;
      scene.screenSpaceCameraController.inertiaTranslate = 0.8;
      scene.screenSpaceCameraController.inertiaSpin = 0.9;
      scene.screenSpaceCameraController.inertiaZoom = 0.8;
      scene.screenSpaceCameraController.maximumMovementRatio = 0.15;

      // Enable terrain
      scene.globe.enableLighting = true;
      scene.globe.baseColor = Color.WHITE;
      scene.globe.showGroundAtmosphere = true;
      scene.globe.atmosphereBrightnessShift = 0.1;
    } catch (error) {
      console.warn('Error setting camera controls:', error);
    }
  };

  const homePlacementLatitude = homePlacement.latitude;
  const homePlacementLongitude = homePlacement.longitude;
  const homePlacementRotation = homePlacement.rotation;

  const setHomePlacementRotation = useCallback(
    (rotation: number) => {
      setHomePlacement((v) => ({ ...v, rotation: rotation }));
    },
    [setHomePlacement]
  );

  const setHomePlacementLatitude = useCallback(
    (latitude: number) => {
      setHomePlacement((v) => ({ ...v, latitude: latitude }));
    },
    [setHomePlacement]
  );

  const setHomePlacementLongitude = useCallback(
    (longitude: number) => {
      setHomePlacement((v) => ({ ...v, longitude: longitude }));
    },
    [setHomePlacement]
  );

  return (
    <Box className="cesium-home-viewer">
      <Box className="container">
        <Fab
          color="primary"
          onClick={() => setShowSettings(!showSettings)}
          sx={{ marginBottom: '8px' }}
        >
          {showSettings ? <RemoveIcon /> : <SettingsIcon />}
        </Fab>

        {showSettings && (
          <>
            <Tooltip placement="left" title="Show/Hide Scenery">
              <Fab
                onClick={() => setShowTileSet(!showTileset)}
                className="button"
              >
                <InsertPhotoIcon />
              </Fab>
            </Tooltip>
            <Tooltip placement="left" title="Rotate Clockwise">
              <Fab
                onClick={() =>
                  setHomePlacementRotation(homePlacementRotation + 4)
                }
                className="button"
              >
                <UpdateIcon />
              </Fab>
            </Tooltip>
            <Tooltip placement="left" title="Rotate Counter Clockwise">
              <Fab
                onClick={() =>
                  setHomePlacementRotation(homePlacementRotation - 4)
                }
                className="button"
              >
                <RestoreIcon />
              </Fab>
            </Tooltip>
            <Tooltip placement="left" title="Move Up">
              <Fab
                onClick={() =>
                  setHomePlacementLatitude(homePlacementLatitude + 0.00001)
                }
                className="button"
              >
                <ArrowUpwardIcon />
              </Fab>
            </Tooltip>
            <Tooltip placement="left" title="Move Down">
              <Fab
                onClick={() =>
                  setHomePlacementLatitude(homePlacementLatitude - 0.00001)
                }
                className="button"
              >
                <ArrowDownwardIcon />
              </Fab>
            </Tooltip>
            <Tooltip placement="left" title="Move Left">
              <Fab
                onClick={() =>
                  setHomePlacementLongitude(homePlacementLongitude - 0.00001)
                }
                className="button"
              >
                <ArrowBackIcon />
              </Fab>
            </Tooltip>
            <Tooltip placement="left" title="Move Right">
              <Fab
                onClick={() =>
                  setHomePlacementLongitude(homePlacementLongitude + 0.00001)
                }
                className="button"
              >
                <ArrowForwardIcon />
              </Fab>
            </Tooltip>
          </>
        )}
      </Box>

      {homeModel && (
        <Viewer
          ref={(viewer) => {
            if (viewer) {
              ref.current = viewer;
              if (viewer.cesiumElement) {
                setCameraControls();
              }
            }
          }}
          homeButton={false}
          sceneModePicker={false}
          infoBox={false}
          geocoder={false}
          baseLayerPicker={false}
          timeline={false}
          fullscreenButton={false}
          full
          terrainProvider={CesiumTerrainProvider.fromIonAssetId(1)}
          scene3DOnly={true}
          sceneMode={3}
        >
          <CameraLookAt
            offset={offset}
            target={Cartesian3.fromDegrees(
              homePlacementLongitude,
              homePlacementLatitude,
              0
            )}
            once={true}
          />
          <Entity
            orientation={Transforms.headingPitchRollQuaternion(
              Cartesian3.fromDegrees(
                homePlacementLongitude,
                homePlacementLatitude,
                0
              ),
              new HeadingPitchRoll(
                degreesToRadians(
                  homePlacementRotation +
                    (selectedHomeModelSku === 'la_mer_01' ? 180 : 90)
                ),
                0,
                0
              )
            )}
            position={Cartesian3.fromDegrees(
              homePlacementLongitude,
              homePlacementLatitude,
              0
            )}
          >
            <ModelGraphics
              scale={1}
              heightReference={HeightReference.CLAMP_TO_TERRAIN}
              uri={homeModel.preSignedUrl}
            />
          </Entity>
          <Cesium3DTileset
            show={showTileset}
            onReady={handleReady}
            onError={(error) => {
              console.error('Error loading 3D tiles:', error);
            }}
            url={tilesUrl}
          />
        </Viewer>
      )}
    </Box>
  );
};
